import {Lang} from 'yougile-lang'
import {
  ContentType,
  ContentTypeUtil,
  FilenameUtil,
  HTMLUtil,
  UIUtil,
  Util
} from 'yougile-util'
import {getIdWithPrefix} from 'yougile-util'
import {
  ChatMessage,
  ChatMessageJSON,
  DataObjectId,
  getMessageAssignIds,
  GroupChat,
  GTDStage,
  Hub,
  MessageTextChunk,
  MessageTextParams,
  SandboxTree,
  SystemMessageUtils
} from 'yougile-model'
import {Accounts} from '@client/accounts'
import {DepartmentsClient} from '@client/departments-client'
import {LazyLoadClient} from '@client/lazy-load-client'
import {PermissionsChecker} from '@client/permissions-checker'
import {Sync} from '@client/sync'
import {FilePreview} from '@common/file-preview'
import {getPath} from '@components/statistics/stats-timeline/utils/utils'
import {EmojiUtils} from '@extensions/emoji-utils'
import {idTaskRegexpSimple} from './chat-utils'
import {idTasksApi} from './id-tasks/id-tasks-store'
import {Task} from './task'

export interface FileViewData {
  url: string
  file?: File
  contentType: ContentType
  timestamp: number
}

export interface MessageLinksData {
  urls: string[]
  timestamp: number
}

export class MessageUtils {
  static rxTaskLink =
    /https?:\/\/[-a-z0-9@:%._+~#=]{2,262}\/team(\/)?([^\s,()<>]+)(?=[^\S]|[,;:<>]|$)/gi

  static renderHtmlForView(box: SandboxTree, message: ChatMessage): string {
    const params = message.getParams()
    if (message.isTextHtml()) {
      const html = message.getTextHtml()
      return MessageUtils.messageTextHtmlToViewMarkup(box, html, params)
    } else {
      const text = SystemMessageUtils.isSystemMessage(message)
        ? MessageUtils.systemMessageText(message)
        : message.text
      return MessageUtils.messageTextToViewMarkup(box, text, params)
    }
  }

  private static messageTextHtmlToViewMarkup(
    box: SandboxTree,
    textHtml: string,
    params?: MessageTextParams
  ): string {
    // only allowed tags are allowed in message textHtml content
    const resHtml = HTMLUtil.sanitizeHtml(textHtml.trim())
    // `p` tags provide line breaks, no need to replace `\n` to line breaks
    return MessageUtils.messageToViewMarkup(box, resHtml, params)
  }

  private static messageTextToViewMarkup(
    box: SandboxTree,
    text: string,
    params?: MessageTextParams
  ): string {
    // no HTML is allowed in message text content
    let resHtml = HTMLUtil.escapeHtml(text.trim())
    resHtml = MessageUtils.messageToViewMarkup(box, resHtml, params)
    resHtml = HTMLUtil.lineBreakToBr(resHtml)
    return resHtml
  }

  private static messageToViewMarkup(
    box: SandboxTree,
    textOrHtml: string,
    params?: MessageTextParams
  ): string {
    let resHtml = textOrHtml
    resHtml = MessageUtils.replaceIdTaskPartialForView(box, resHtml)
    resHtml = MessageUtils.replaceTaskLinksForView(box, resHtml)
    resHtml = MessageUtils.replaceIdTaskLinksForView(box, resHtml)
    resHtml = UIUtil.highlightLinksInText(resHtml)
    resHtml = UIUtil.highlightSpecificLinksInText(resHtml)
    resHtml = FilePreview.replaceEncodedWithHtmlForView(resHtml)
    resHtml = MessageUtils.replaceMentionsForView(resHtml, params)
    resHtml = EmojiUtils.parse(resHtml)
    return resHtml
  }

  static renderHtmlForEdit(message: ChatMessage): string {
    const params = message.getParams()
    let text: string

    if (message.isTextHtml()) {
      text = MessageUtils.messageTextHtmlToEditMarkup(
        message.getTextHtml(),
        params
      )
    } else {
      text = MessageUtils.messageTextToEditMarkup(message.text, params)
    }

    text = FilenameUtil.removeFilenames(text)

    return text.trim().replace(/(<p>\s*<\/p>)+$/g, '')
  }

  // this method is used in doBuildIndex and search-results
  static renderHtmlForSearch(
    _box: SandboxTree,
    message: ChatMessageJSON
  ): string {
    return message.text
    // let text = HTMLUtil.escapeHtml(message.text.trim())
    // text = FilePreview.encodedToFilenames(text)

    // delete mentions
    // text = MessageUtils.replaceMentions(text, message.getParams(), chunk =>
    //   ' '.repeat(chunk.replacement.length)
    // )
    // todo: try fixing this later
    // const replacer = (hub: Hub, id?: string) => id ?? hub.title
    // text = MessageUtils.replaceIdTaskLinks(box, text, replacer)
    // text = MessageUtils.replaceTaskLinks(box, text, replacer)
    // return text
  }

  private static messageTextHtmlToEditMarkup(
    text: string,
    params?: MessageTextParams
  ): string {
    text = text.trim()
    // only allowed tags are allowed in message textHtml content
    text = HTMLUtil.sanitizeHtml(text)
    // todo: fix image&document downcasting and reenable this
    // text = FilePreview.replaceEncodedWithHtmlForEdit(text)
    // textHtml content already has line breaks provided by `p` tags
    text = MessageUtils.replaceMentionsForView(text, params)
    text = EmojiUtils.parse(text)
    text = EmojiUtils.replaceEmojiForEdit(text)
    return text
  }

  private static messageTextToEditMarkup(
    text: string,
    params?: MessageTextParams
  ): string {
    text = text.trim()
    // no HTML is allowed in message text content
    text = HTMLUtil.escapeHtml(text)
    // todo: fix image&document downcasting and reenable this
    // text = FilePreview.replaceEncodedWithHtmlForEdit(text)
    text = HTMLUtil.lineBreakToParagraph(text)
    text = MessageUtils.replaceMentionsForView(text, params)
    text = EmojiUtils.parse(text)
    text = EmojiUtils.replaceEmojiForEdit(text)
    return text
  }

  static getMessagesLinks = (msgs: ChatMessage[]): MessageLinksData[] => {
    const links: MessageLinksData[] = []
    for (const msg of msgs) {
      if (SystemMessageUtils.isSystemMessage(msg)) {
        continue
      }

      links.push({
        urls: msg.getLinksForAttachments(),
        timestamp: msg.timestamp
      })
    }
    return links
  }

  static getLinkIndex = (
    links: MessageLinksData[],
    selected: string
  ): number => {
    const urls = links.reduce((a, c) => {
      return [...a, ...c.urls]
    }, [])
    const selectedOriginal = FilenameUtil.originalFileFromUrl(selected)
    return urls.findIndex(
      link => FilenameUtil.originalFileFromUrl(link) === selectedOriginal
    )
  }

  static getFileViewData = (links: MessageLinksData[]): FileViewData[] => {
    const data: FileViewData[] = []
    for (const urls of links) {
      for (const link of urls.urls) {
        const extension = FilenameUtil.originalFileFromUrl(link)
          .split('.')
          ?.pop()
          ?.split('?')[0]
        if (!extension) {
          continue
        }
        data.push({
          url: link,
          contentType: ContentTypeUtil.getContentTypeByExtension(extension),
          timestamp: urls.timestamp
        })
      }
    }
    return data
  }

  private static replaceTaskLinksForView(
    box: SandboxTree,
    text: string
  ): string {
    return MessageUtils.replaceTaskLinks(box, text, this.replaceDomTaskLink)
  }

  private static replaceIdTaskLinksForView(
    box: SandboxTree,
    text: string
  ): string {
    return MessageUtils.replaceIdTaskLinks(box, text, this.replaceDomTaskLink)
  }

  private static replaceIdTaskPartialForView(
    box: SandboxTree,
    text: string
  ): string {
    return MessageUtils.replaceIdTaskPartial(box, text, this.replaceDomTaskLink)
  }

  public static replaceIdTaskPartialWithOldFormat(
    box: SandboxTree,
    text: string
  ): string {
    return MessageUtils.replaceIdTaskPartial(box, text, Task.getLinkOldFormat)
  }

  public static replaceIdTaskLinksWithOldFormat(
    box: SandboxTree,
    text: string
  ): string {
    return MessageUtils.replaceIdTaskLinks(box, text, Task.getLinkOldFormat)
  }

  private static replaceTaskLinks(
    box: SandboxTree,
    text: string,
    replacer: (hub: Hub, prefix?: string) => string
  ): string {
    const currentBox = Sync.getCurrentSandbox()
    return text.replace(MessageUtils.rxTaskLink, (...rest) => {
      const [matched, , group1] = rest

      if (!group1?.includes('#chat:')) {
        return matched
      }

      const shortId = group1.split('#chat:').pop()
      const hub =
        (box.findByShortId(shortId) as Hub) ??
        (currentBox.findByShortId(shortId) as Hub)
      if (!hub) {
        return matched
      }

      if (currentBox.getType(hub) === 'task') {
        void LazyLoadClient.waitLoad(box, hub.id, 'message-task-link')

        if (Util.isUUID(hub.title) || hub.title?.startsWith('.')) {
          return matched
        }

        const idTask = idTasksApi.getIdTask(hub.id)
        return replacer(hub, idTask ? `${idTask} ` : '')
      } else {
        // case for group chats
        return replacer(hub)
      }
    })
  }

  private static replaceIdTaskLinks(
    box: SandboxTree,
    text: string,
    replacer: (hub: Hub, prefix: string) => string
  ): string {
    const currentBox = Sync.getCurrentSandbox()
    return text.replace(MessageUtils.rxTaskLink, (...rest) => {
      const [matched, , group1] = rest

      if (!group1) {
        return matched
      }

      const part1 = group1.toUpperCase()
      const hash = part1.split('#').pop()
      const [canBePrefix, idTask] = hash.split('-')

      if (isNaN(idTask)) {
        return matched
      }

      const taskId = idTasksApi.getReverseIdTask(idTask, canBePrefix)
      if (!taskId) {
        return matched
      }

      const hub = box.getHub(taskId) ?? currentBox.getHub(taskId)
      if (!hub) {
        return matched
      }

      if (Util.isUUID(hub.title) || hub.title?.startsWith('.')) {
        return matched
      }

      void LazyLoadClient.waitLoad(box, hub.id, 'message-task-link')

      const idWithPrefix = getIdWithPrefix(idTask, canBePrefix)
      return replacer(hub, `${idWithPrefix} `)
    })
  }

  private static replaceIdTaskPartial(
    box: SandboxTree,
    text: string,
    replacer: (hub: Hub, prefix?: string, postfix?: string) => string
  ): string {
    const currentBox = Sync.getCurrentSandbox()
    return text.replace(idTaskRegexpSimple, (...rest) => {
      const [matched, group1, group2] = rest

      if (!group1) {
        return matched
      }

      if (isNaN(group2)) {
        return matched
      }

      const canBePrefix = group1.toUpperCase()
      const taskId = idTasksApi.getReverseIdTask(group2, canBePrefix)
      if (!taskId) {
        return matched
      }

      const hub = box.getHub(taskId) ?? currentBox.getHub(taskId)
      if (!hub) {
        return matched
      }

      if (Util.isUUID(hub.title) || hub.title?.startsWith('.')) {
        return matched
      }

      void LazyLoadClient.waitLoad(box, hub.id, 'mssage-task-link')

      const idWithPrefix = idTasksApi.getIdTask(taskId)
      return replacer(hub, `${idWithPrefix} `)
    })
  }

  private static replaceDomTaskLink(
    hub: Hub,
    prefix = '',
    postfix = ''
  ): string {
    // `span` is used because only inline elements can be placed inside `p` tag
    const el = document.createElement('span')
    el.className = 'message-item-new__task-link reset'
    el.dataset.company = hub.companyId
    el.dataset.task = hub.id

    const title = HTMLUtil.trimLineBreaks(GroupChat.getTitle(hub) || hub.title)
    const content = `${prefix}${title}`
    el.textContent = content
    el.title = content

    const wrapper = document.createElement('div')
    wrapper.append(el)

    if (postfix) {
      // is used because previous span use truncate
      const postfixEl = document.createElement('span')
      postfixEl.textContent = postfix
      wrapper.append(postfixEl)
    }

    return wrapper.innerHTML
  }

  private static replaceMentionsForView(
    text: string,
    params?: MessageTextParams
  ): string {
    return MessageUtils.replaceMentions(text, params, chunk => {
      const userId = chunk.data.userId
      // `span` is used because only inline elements can be placed inside `p` tag
      const el = document.createElement('span')
      el.className = 'message-item-new__mention'
      el.dataset.user = userId
      el.textContent = `@${Accounts.getName(userId).trim()}`

      const wrapper = document.createElement('div')
      wrapper.appendChild(el)
      return wrapper.innerHTML
    })
  }

  public static replaceMentions(
    text: string,
    params: MessageTextParams | undefined,
    replacer: (chunk: MessageTextChunk) => string
  ): string {
    params?.chunks.forEach(c => {
      const {userId} = c.data
      if (c.type === 'user' && userId) {
        text = text.replace(
          new RegExp(Util.escapeRegexp(c.replacement), 'gi'),
          () => replacer(c)
        )
      }
    })

    return text
  }

  static systemMessageText(message: ChatMessage): string {
    const who = Accounts.getName(message.properties.actionBy as DataObjectId)
    if (SystemMessageUtils.isDeadline(message)) {
      return SystemMessageUtils.deadlineText(message)
    } else if (SystemMessageUtils.isDeadlineNotice(message)) {
      return SystemMessageUtils.deadlineNoticeText(message)
    } else if (SystemMessageUtils.isAssignment(message)) {
      const assigned = getMessageAssignIds(message).map(x =>
        Accounts.getName(x)
      )
      return SystemMessageUtils.assignmentText(message, assigned, who)
    } else if (SystemMessageUtils.isGtd(message)) {
      const after = message.properties.after as GTDStage | undefined
      return SystemMessageUtils.gtdText(message, who, after === 'done')
    } else if (SystemMessageUtils.isMove(message)) {
      const box = Sync.get(message)
      const hierarchy = DepartmentsClient.getHierarchy(box)
      const objFrom = box.getHub(message.properties.from as DataObjectId)
      const objTo = box.getHub(message.properties.to as DataObjectId)
      if (!objFrom && !objTo) {
        return '???'
      } else {
        // Same as tile.tsx, ObjectName/getHint, 'move' case
        const from = PermissionsChecker.checkShow(hierarchy, objFrom, {})
          ? getPath({obj: objFrom, box, withName: true})
          : Lang.msg.statsTimeline.inaccessibleLocation
        const to = getPath({obj: objTo, box, withName: true})
        return SystemMessageUtils.moveText(message, who, from, to)
      }
    } else {
      return message.text
    }
  }
}
